import { useEffect, useState } from "react";
import { launcherAvailable } from "./launcherApi.js";

interface Survey {
  device?: { manufacturer?: string; model?: string; android?: string };
  settings?: Record<string, string | null | string[]>;
  apps?: Array<{ tv?: boolean }>;
  home?: { current?: string | null; googleTv?: boolean };
  inputs?: { list?: Array<{ type?: string; label?: string; passthrough?: boolean }> };
  bluetooth?: { present?: boolean; on?: boolean | null; bonded?: number | null };
  network?: { transport?: string; wifiOn?: boolean | null };
  display?: { current?: string; modes?: string[] };
  access?: Record<string, boolean>;
}

interface CapacitorLike {
  nativePromise?: (plugin: string, method: string, options: Record<string, unknown>) => Promise<unknown>;
}

const ACCESS_NAMES: Record<string, string> = {
  overlay: "display over other apps",
  usageAccess: "usage access",
  notificationAccess: "notifications",
  accessibility: "accessibility",
  writeSettings: "modify system settings",
  installApps: "install apps",
  bluetoothConnect: "Bluetooth",
  queryAllPackages: "see every app",
};

/**
 * What this box has and allows, read-only (launcher survey, roadmap Section 32 step 0). For a
 * box nobody can reach with a computer, such as the projector: the owner reads it off the
 * screen. Nothing here can be pressed, and it changes nothing.
 */
export function ThisBoxCard() {
  const [survey, setSurvey] = useState<Survey | null>(null);

  useEffect(() => {
    if (!launcherAvailable()) return;
    const cap = (globalThis as { Capacitor?: CapacitorLike }).Capacitor;
    void cap?.nativePromise?.("Launcher", "survey", {}).then((answer) => setSurvey(answer as Survey)).catch(() => undefined);
  }, []);

  if (!survey) return null;
  const screens = Object.entries(survey.settings ?? {}).filter(([key]) => !key.startsWith("_"));
  const opening = screens.filter(([, value]) => value !== null).length;
  const apps = survey.apps ?? [];
  const tvApps = apps.filter((app) => app.tv).length;
  const inputs = (survey.inputs?.list ?? []).filter((input) => input.passthrough);
  const held = Object.entries(survey.access ?? {})
    .filter(([key, value]) => value && key in ACCESS_NAMES)
    .map(([key]) => ACCESS_NAMES[key]);
  const rows: Array<[string, string]> = [
    ["Box", `${survey.device?.manufacturer ?? ""} ${survey.device?.model ?? ""}, Android ${survey.device?.android ?? "?"}`.trim()],
    ["Home app", `${survey.home?.current ?? "unknown"}${survey.home?.googleTv ? " (Google TV)" : ""}`],
    ["Android settings", `${survey.settings?.all ? "main page opens" : "main page does not open"}; ${opening} of ${screens.length} screens open`],
    ["Apps", `${apps.length} (${tvApps} TV apps, ${apps.length - tvApps} phone-style)`],
    ["Inputs", inputs.length > 0 ? inputs.map((input) => input.label || input.type).join(", ") : "none"],
    ["Bluetooth", !survey.bluetooth?.present ? "not on this box" : `${survey.bluetooth.on ? "on" : "off"}${typeof survey.bluetooth.bonded === "number" ? `, ${survey.bluetooth.bonded} paired` : ""}`],
    ["Network", survey.network?.transport ?? "unknown"],
    ["Screen", `${survey.display?.current ?? "?"} (${new Set(survey.display?.modes ?? []).size} modes)`],
    ["SliqueTV may", held.length > 0 ? held.join(", ") : "nothing extra yet"],
  ];

  return (
    <div className="settings-card">
      <h2 className="settings-card-title">This box</h2>
      <p className="settings-card-desc">What this TV box has, as SliqueTV sees it. For help with a box that has no Settings app of its own.</p>
      <dl className="this-box">
        {rows.map(([label, value]) => (
          <div key={label} className="this-box__row">
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
